// src/utils/taskStatus.ts

export type TaskStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'retrying';

/**
 * Check if task status requires polling/monitoring
 */
export const isActiveStatus = (status: TaskStatus): boolean => {
  return ['queued', 'processing', 'retrying'].includes(status);
};

/**
 * Check if task status is final (no more changes expected)
 */
export const isFinalStatus = (status: TaskStatus): boolean => {
  return ['completed', 'failed'].includes(status);
};

/**
 * Check if task can be retried
 */
export const canRetryTask = (status: TaskStatus, uploadTimestamp?: string): boolean => {
  if (['failed', 'retrying'].includes(status)) {
    return true;
  }
  
  // Allow retry for queued tasks older than 24 hours
  if (status === 'queued' && uploadTimestamp) {
    const uploadDate = new Date(uploadTimestamp);
    const now = new Date();
    const hoursDiff = (now.getTime() - uploadDate.getTime()) / (1000 * 60 * 60);
    return hoursDiff > 24;
  }
  
  return false;
};

/**
 * Check if task has results available to view
 */
export const hasResults = (status: TaskStatus): boolean => {
  return status === 'completed' || status === 'failed'; // Allow viewing failed analyses too
};

/**
 * Check if task needs polling
 */
export const needsPolling = (status: TaskStatus): boolean => {
  return isActiveStatus(status);
};

/**
 * Generic status display information
 */
export interface TaskStatusInfo {
  color: string;
  severity: 'info' | 'success' | 'warning' | 'error';
  label: string;
}

export const getTaskStatusInfo = (status: TaskStatus): TaskStatusInfo => {
  switch (status) {
    case 'queued':
      return { color: 'medium', severity: 'info', label: 'Queued' };
    case 'processing':
      return { color: 'primary', severity: 'info', label: 'Processing' };
    case 'completed':
      return { color: 'success', severity: 'success', label: 'Completed' };
    case 'failed':
      return { color: 'danger', severity: 'error', label: 'Failed' };
    case 'retrying':
      return { color: 'warning', severity: 'warning', label: 'Retrying' };
    default:
      return { color: 'medium', severity: 'info', label: 'Unknown' };
  }
};

/**
 * Map backend Celery status to our simplified TaskStatus
 */
export const mapCeleryStatus = (celeryStatus: string): TaskStatus => {
  if (!celeryStatus || celeryStatus === 'null' || celeryStatus === 'None' || celeryStatus === 'undefined') {
    console.warn(`⚠️ mapCeleryStatus received invalid status: "${celeryStatus}"`);
    return 'queued'; // Default to queued for new uploads
  }

  const normalizedStatus = celeryStatus.toUpperCase().trim();

  console.log(`🔄 Mapping Celery status: "${celeryStatus}" → "${normalizedStatus}"`);

  switch (normalizedStatus) {
    case 'PENDING':
    case 'QUEUED':
      return 'queued';
    case 'STARTED':
    case 'PROCESSING':
      return 'processing';
    case 'SUCCESS':
    case 'COMPLETED':
      return 'completed';
    case 'FAILURE':
    case 'FAILED':
      return 'failed';
    case 'RETRY':
    case 'RETRYING':
      return 'retrying';
    case 'REVOKED':
    case 'UNKNOWN':
    case 'NULL':
    case 'NONE':
      console.warn(`⚠️ Revoked/Unknown/Null Celery status: "${celeryStatus}" - treating as queued`);
      return 'queued';
    default:
      console.warn(`⚠️ Unknown Celery status: "${celeryStatus}" - treating as queued`);
      return 'queued'; // Treat unknown states as queued instead of failed
  }
};